/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import type { FeatureFlags, LanguageFeatures } from './feature-flags';

export type Noop = () => void;

export const enum Role {
  MANAGER = 'Manager',
  WRITER = 'Writer',
  READER = 'Reader',
  CREATOR = 'Creator',
  UNAVAILABLE = 'Unavailable'
}

export const enum CustomRole {
  LOGS_READER = 'logsReader',
  LOGS_READER_DEV = 'logsReaderDev',
  VERSION_MAKER = 'versionMaker',
  VERSION_MAKER_DEV = 'versionMakerDev'
}

export const enum ApiStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCEEDED = 'succeeded',
  ERROR = 'error'
}

export const enum LoaderStates {
  IDLE = 'idle',
  INACTIVE = 'inactive',
  ACTIVE = 'active',
  ERROR = 'error',
  SUCCESS = 'finished'
}

export const enum TestingStatus {
  COMPLETE = 'completed',
  IN_PROGRESS = 'in_progress',
  IN_QUEUE = 'in_queue',
  FAILED = 'failed'
}

export interface PaginatedResult {
  refresh_url?: string;
  next_url?: string;
  total?: number;
  matched?: number;
}

export type Endpoints = {
  apiURL: string;
  authenticate: string;
  bluemix: string;
  content: string;
  store: string;
  beta: string;
  feedback: string;
  logout: string;
  webChat: string;
  preview: string;
};

export type BuildInfo = {
  name: string;
  description: string;
  version: string;
  nodeEnv: string;
  imageTag: string;
  buildTime: string;
  buildDate: string;
  gitCommit: string;
};

export type WebSupport = {
  integrationID: string;
  region: string;
  serviceInstanceID: string;
};

export const enum DeployEnv {
  DEVAWS = 'devaws',
  DEV = 'dev',
  STAGE = 'stage',
  STAGEAWS = 'stgaws',
  PREPRD = 'preprd',
  PREPRODUCTIONAWS = 'wo-wa-use1-ra1',
  PRODUCTION = 'production', // used for both WA standalone and WxO on IBM Cloud
  PRODUCTIONAWS = 'wo_wa_use1_rp1',
  FRAPRODUCTIONAWS = 'wo_wa_euc1_rp1',
  SINPRODUCTIONAWS = 'wo_wa_ase1_rp1',
  MUMPRODUCTIONAWS = 'wo_wa_as1_rp1',
  WDCPRD = 'wdcprd',
  FRAPRD = 'fraprd',
  LONPRD = 'lonprd',
  SYDPRD = 'sydprd',
  TOKPRD = 'tokprd'
}

export type WindowConversation = {
  endpoints: Endpoints;
  cloudConsoleUrl: string;
  features: FeatureFlags;
  languages: LanguageFeatures[];
  segment: {
    key: string;
    src: string;
    cookies: {
      [key: string]: string;
    };
  };
  deployEnv: DeployEnv;
  integrations: {
    intercom: {
      client_id: string;
      oauth_redirect_url: string;
    };
  };
  buildInfo: BuildInfo;
  webExperience: {
    versionHostname: string;
    hostname: string;
  };
  webSupport: WebSupport;
  plusTrialLength: number;
  activityMonitor: { enabled: boolean; interval: number };
  baseUrl: string;
  isIbmCloud: boolean;
  isAws: boolean;
  // The base URL for the Watson Orchestrate server that corresponds to
  // the current environment.
  wxoUrl: string | null;
  // True if Tooling is running as part of watsonx Orchestrate (WXO).
  isWxoEnabled: boolean;
  // True is Tooling is running in a CP4D environment
  isCpd: boolean;
  // True if the slot filling component is enabled in a CP4D environment.
  isSlotFillingEnabled: boolean;
  // On a CPD environment, the prefix that should be used when generating
  // the service endpoint URL for API requests (e.g. "/assistant/cpd-wo-wa").
  cpdPrefix: string;
};

export type WindowAssistant = {
  actionSkillExists: boolean;
  isReadyForWalkMe: boolean;
  isReadyForWalkMePreviewEnd: boolean;
  isReadyForWalkMePreviewStart: boolean;
  isReadyForWalkMeSearchVideo: boolean;
  isReadyForWalkMeStageOne: boolean;
  isReadyForWalkMeStageTwo: boolean;
  isReadyForWalkMeWebChatEnd: boolean;
  isReadyForWalkMeWebChatStart: boolean;
  hasFinishedOnboardingTaskList: boolean;
};

// The different types of WXO experiences (i.e. what UI is rendered): either
// "classic" WXO (the original one) or Agent Builder.

export const enum WxoExperience {
  CLASSIC = 'AI_chat',
  AGENT_BUILDER = 'AGENTIC_CHAT'
}

export type WindowWxo = {
  chatVersion: WxoExperience;
};

// Query parameters that may be included in the referer URL when Tooling
// is loaded under WXO as an MFE.
export const enum WxoQueryParams {
  PROVIDER_ID = 'providerId'
}

export type StoreQueryParams = Partial<{
  include_audit: boolean;
  include_count?: boolean;
  append: boolean;
  version: string;
  verbose: boolean;
  sort: string;
  cursor: string;
  page_limit: number;
  new_ia: boolean;
  filter: string;
  private: boolean;
}>;

export type Pagination = {
  refresh_url: string;
  next_url?: string;
  total?: number;
  matched?: number;
  refresh_cursor?: string;
  next_cursor?: string;
};

export type CrnScope = {
  account?: string;
  org?: string;
  space?: string;
};

export type Crn = {
  crn: string;
  version: string;
  cname: string;
  ctype: string;
  service_name: string;
  region: string;
  scope: CrnScope;
  service_instance: string;
  resource_type: string;
  resource: string;
};

export const enum AuthQueryParam {
  SUCCESS_REDIRECT = 'successRedirect',
  FAILURE_REDIRECT = 'failureRedirect',
  INSTANCE = 'instance'
}

export const enum GeneralQueryParam {
  INSTANCE_CRN = 'instance_crn',
  SPEECH_INSTANCE_CRN = 'speech_instance_crn',
  DISCOVERY_INSTANCE_CRN = 'discovery_instance_crn'
}

export const enum CarbonTextInputType {
  PASSWORD = 'password',
  TEXT = 'text'
}

export const enum SpeechQueryParam {
  INSTANCE_ID = 'instanceId',
  PITCH_PERCENTAGE = 'pitch_percentage',
  RATE_PERCENTAGE = 'rate_percentage',
  TEXT = 'text',
  VOICE = 'voice'
}
